import Navigate             from "Dashboard/Core/Navigate";
import NLS                  from "Dashboard/Core/NLS";
import Store                from "Dashboard/Core/Store";
import DateTime             from "Dashboard/Utils/DateTime";
import Utils                from "Dashboard/Utils/Utils";

// The API
import {
    CourseSubscription,
} from "Utils/API";



// The initial State
const initialState = {
    loading   : true,
    error     : false,
    edition   : 0,
    canCreate : false,
    canEdit   : false,
    canFilter : false,
    canImport : false,
    canExport : false,
    list      : [],
    total     : 0,
    course    : {},
    elem      : {
        persons : [],
    },
    questions : [],
    members   : [],
    statuses  : [],
    filters   : {
        search   : "",
        status   : "",
        fromDate : "",
        toDate   : "",
    },
    sort      : {
        orderBy  : "number",
        orderAsc : 1,
        page     : 0,
        amount   : 50,
    },
};



// The Actions
const actions = {
    /**
     * Starts the Loader
     * @param {Function} dispatch
     * @returns {Void}
     */
    startLoader(dispatch) {
        dispatch({ type : "COURSE_SUBSCRIPTION_LOADING" });
    },

    /**
     * Fetches the Course Subscription List
     * @param {Function} dispatch
     * @param {String=}  type
     * @param {Number=}  elemID
     * @param {Object=}  filters
     * @param {Object=}  sort
     * @returns {Promise}
     */
    async fetchList(dispatch, type = "", elemID = 0, filters = {}, sort = {}) {
        const params = { ...filters, ...sort };
        Navigate.unsetParams(params);
        if (type === "COURSE") {
            params.courseID = elemID;
        }

        const data   = await CourseSubscription.getAll(params);
        data.filters = filters;
        data.sort    = sort;
        dispatch({ type : "COURSE_SUBSCRIPTION_LIST", data });
    },

    /**
     * Fetches a single Course Subscription
     * @param {Function} dispatch
     * @param {Number}   subscriptionID
     * @returns {Promise}
     */
    async fetchElem(dispatch, subscriptionID) {
        const data = await CourseSubscription.getOne({ subscriptionID });
        dispatch({ type : "COURSE_SUBSCRIPTION_ELEM", data });
    },

    /**
     * Fetches the Course Subscription Edit data
     * @param {Function} dispatch
     * @param {Number}   courseID
     * @param {Number=}  memberNumber
     * @param {Boolean=} isInitial
     * @returns {Promise}
     */
    async fetchEditData(dispatch, courseID, memberNumber = 0, isInitial = true) {
        const data = await CourseSubscription.getEditData({ courseID, memberNumber });
        data.isInitial = isInitial;
        dispatch({ type : "COURSE_SUBSCRIPTION_EDIT", data });
    },

    /**
     * Edits/Creates a Course Subscription
     * @param {Function} dispatch
     * @param {Object}   data
     * @returns {Promise}
     */
    editElem(dispatch, data) {
        return CourseSubscription.edit(data);
    },

    /**
     * Marks a Course Subscription as Pending
     * @param {Function} dispatch
     * @param {Number}   subscriptionID
     * @returns {Promise}
     */
    pendingElem(dispatch, subscriptionID) {
        return CourseSubscription.markPending({ subscriptionID });
    },

    /**
     * Nullifies a Course Subscription
     * @param {Function} dispatch
     * @param {Number}   subscriptionID
     * @returns {Promise}
     */
    nullifyElem(dispatch, subscriptionID) {
        return CourseSubscription.nullify({ subscriptionID });
    },

    /**
     * Deletes a Course Subscription
     * @param {Function} dispatch
     * @param {Number}   subscriptionID
     * @returns {Promise}
     */
    deleteElem(dispatch, subscriptionID) {
        return CourseSubscription.delete({ subscriptionID });
    },
};



/**
 * Parses a single Element
 * @param {Object} elem
 * @returns {Object}
 */
function parseElem(elem) {
    elem.createdTimeText = DateTime.formatString(elem.createdTime, false);
    elem.name            = `${elem.memberNumber} - ${elem.memberName}`;
    elem.numberText      = `#${elem.number}`;
    elem.personsText     = `${elem.approvedPersons} / ${elem.totalPersons}`;

    for (const person of elem.persons) {
        person.age = DateTime.getAge(person.birthDate);

        person.personTypeName = NLS.get("SELECT_PERSON_TYPES", person.personType);
        if (person.memberNumber) {
            person.personTypeName = `<b>${person.personTypeName}</b>: ${person.memberNumber}`;
        }

        if (person.memberDni) {
            person.document = `${NLS.get("SELECT_DOCUMENT_TYPES", 1)}: ${person.memberDni}`;
        } else {
            person.document = `${NLS.get("SELECT_DOCUMENT_TYPES", person.documentType)}: ${person.documentValue}`;
        }
    }
    return elem;
}

/**
 * The Reducer
 * @param {Object=} state
 * @param {Object=} action
 * @returns {Object}
 */
const reducer = (state = initialState, action = {}) => {
    if (Utils.hasError(action, "COURSE_SUBSCRIPTION_LIST", "COURSE_SUBSCRIPTION_ELEM")) {
        return { ...state, loading : false, error : true };
    }

    switch (action.type) {
    case "COURSE_SUBSCRIPTION_LOADING":
        return {
            ...state,
            loading   : true,
        };

    case "COURSE_SUBSCRIPTION_LIST":
        return {
            ...state,
            loading   : false,
            error     : false,
            canCreate : action.data.canCreate,
            canEdit   : action.data.canEdit,
            canFilter : action.data.canFilter,
            canImport : action.data.canImport,
            canExport : action.data.canExport,
            list      : Utils.parseList(action.data.list, parseElem),
            total     : action.data.total,
            course    : action.data.course,
            statuses  : action.data.statuses,
            filters   : action.data.filters,
            sort      : action.data.sort,
        };

    case "COURSE_SUBSCRIPTION_ELEM":
        return {
            ...state,
            error     : false,
            edition   : state.edition + 1,
            elem      : parseElem(action.data.elem),
            questions : action.data.questions,
            members   : action.data.members,
            statuses  : action.data.statuses,
        };

    case "COURSE_SUBSCRIPTION_EDIT":
        return {
            ...state,
            error     : false,
            edition   : state.edition + (action.data.isInitial ? 1 : 0),
            questions : action.data.questions,
            members   : action.data.members,
        };

    default:
        return state;
    }
};




// The public API
export default Store.createSlice(initialState, actions, reducer);
